import React, { useState, useEffect } from 'react';

const COLORS = { black: '#0A0A0A', white: '#F5F5F0', gold: '#C9A84C', gray: '#888' };

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Fallback mock data — used when VITE_API_BASE_URL is not configured.
const MOCK_ORDERS = [
  {
    id: 'ord_mock001',
    status: 'claimed',
    qr_code: 'XQ7R4P',
    items: [{ product_id: 'tee-black-m', quantity: 1, size: 'M' }],
    created_at: '2026-03-25T14:00:00Z',
  },
  {
    id: 'ord_mock002',
    status: 'pending',
    qr_code: 'KN2M8A',
    items: [{ product_id: 'hoodie-black-l', quantity: 1, size: 'L' }],
    created_at: '2026-03-22T09:30:00Z',
  },
];

const STATUS_STYLES = {
  pending:   { label: 'PENDING',   color: '#C9A84C', bg: 'rgba(201,168,76,0.08)' },
  paid:      { label: 'PAID',      color: '#6dbf8b', bg: 'rgba(109,191,139,0.08)' },
  claimed:   { label: 'CLAIMED',   color: '#6dbf8b', bg: 'rgba(109,191,139,0.08)' },
  fulfilled: { label: 'FULFILLED', color: 'rgba(255,255,255,0.5)', bg: 'rgba(255,255,255,0.04)' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || { label: status.toUpperCase(), color: COLORS.gray, bg: 'rgba(255,255,255,0.04)' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '0.2rem 0.6rem',
      fontSize: '0.62rem',
      fontWeight: 700,
      letterSpacing: '0.14em',
      color: s.color,
      backgroundColor: s.bg,
      border: `1px solid ${s.color}33`,
    }}>
      {s.label}
    </span>
  );
}

function SkeletonRow() {
  return (
    <div style={{
      padding: '1.25rem 1.5rem',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    }}>
      {[120, 80, 60].map((w, i) => (
        <div key={i} style={{
          height: '10px',
          width: `${w}px`,
          backgroundColor: 'rgba(255,255,255,0.06)',
          borderRadius: '2px',
        }} />
      ))}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function formatItems(items) {
  if (!Array.isArray(items) || items.length === 0) return '—';
  return items
    .map((i) => [i.quantity > 1 ? `${i.quantity}×` : '', i.product_id, i.size ? `(${i.size})` : ''].filter(Boolean).join(' '))
    .join(', ');
}

function OrderRow({ order }) {
  const isClaimed   = order.status === 'claimed' || order.status === 'fulfilled';
  const isRedeemable = order.status === 'paid';

  return (
    <div style={{
      padding: '1.25rem 1.5rem',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: '0.75rem 1.5rem',
      alignItems: 'start',
    }}>
      {/* Left: order details */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <StatusBadge status={order.status} />
          <span style={{ fontSize: '0.7rem', color: COLORS.gray, letterSpacing: '0.06em' }}>
            {formatDate(order.created_at)}
          </span>
        </div>

        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', margin: 0 }}>
          {formatItems(order.items)}
        </p>

        {order.qr_code && (
          <p style={{ fontSize: '0.72rem', color: COLORS.gray, margin: 0 }}>
            QR code:{' '}
            <span style={{ fontWeight: 700, color: COLORS.gold, letterSpacing: '0.1em' }}>
              {order.qr_code}
            </span>
          </p>
        )}

        <p style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.25)', margin: 0, letterSpacing: '0.04em' }}>
          #{order.id.slice(0, 16).toUpperCase()}
        </p>
      </div>

      {/* Right: action */}
      <div style={{ display: 'flex', alignItems: 'center', paddingTop: '0.15rem' }}>
        {isClaimed && order.qr_code && (
          <a
            href={`/qr/${order.qr_code}`}
            style={{
              display: 'inline-block',
              padding: '0.45rem 1rem',
              border: `1px solid ${COLORS.gold}`,
              color: COLORS.gold,
              textDecoration: 'none',
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.14em',
              whiteSpace: 'nowrap',
            }}
          >
            EDIT QR
          </a>
        )}

        {isRedeemable && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', alignItems: 'flex-end' }}>
            <span style={{ fontSize: '0.65rem', color: COLORS.gold, fontWeight: 600, letterSpacing: '0.1em' }}>
              CLAIM READY
            </span>
            <span style={{ fontSize: '0.6rem', color: COLORS.gray, maxWidth: '160px', textAlign: 'right', lineHeight: 1.4 }}>
              Check your email for the claim link.
            </span>
          </div>
        )}

        {order.status === 'pending' && (
          <span style={{ fontSize: '0.62rem', color: COLORS.gray, letterSpacing: '0.08em' }}>
            AWAITING PAYMENT
          </span>
        )}
      </div>
    </div>
  );
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchOrders() {
      if (!API_BASE_URL) {
        await new Promise((r) => setTimeout(r, 300));
        if (!cancelled) {
          setOrders(MOCK_ORDERS);
          setLoading(false);
        }
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}/api/orders`, { credentials: 'include' });

        if (!res.ok) {
          if (res.status === 401) throw new Error('Session expired. Please sign in again.');
          throw new Error(`Failed to load orders (${res.status}).`);
        }

        const data = await res.json();
        if (!cancelled) {
          setOrders(data.orders ?? []);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load orders. Please refresh.');
          setLoading(false);
        }
      }
    }

    fetchOrders();
    return () => { cancelled = true; };
  }, []);

  const isEmpty = !loading && !error && orders.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
      {/* Header */}
      <div>
        <p style={{ fontSize: '0.65rem', letterSpacing: '0.25em', color: COLORS.gold, margin: '0 0 0.5rem', fontWeight: 600 }}>
          HISTORY
        </p>
        <h1 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.01em', color: COLORS.white, margin: 0 }}>
          YOUR ORDERS
        </h1>
        <p style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: COLORS.gray, fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
          Each order includes a QR code linked to your piece. Claim it to take ownership.
        </p>
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
          {[0, 1, 2].map((i) => <SkeletonRow key={i} />)}
        </div>
      )}

      {/* Error state */}
      {error && (
        <div style={{
          padding: '2rem',
          border: '1px solid rgba(224,92,92,0.25)',
          backgroundColor: 'rgba(224,92,92,0.06)',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          alignItems: 'flex-start',
        }}>
          <p style={{ fontSize: '0.82rem', color: '#e05c5c', margin: 0 }}>{error}</p>
          <button
            onClick={() => { setError(null); setLoading(true); }}
            style={{
              padding: '0.6rem 1.5rem',
              backgroundColor: 'transparent',
              border: '1px solid rgba(224,92,92,0.4)',
              color: '#e05c5c',
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.15em',
              cursor: 'pointer',
            }}
          >
            RETRY
          </button>
        </div>
      )}

      {/* Empty state */}
      {isEmpty && (
        <div style={{ padding: '5rem 2rem', textAlign: 'center', border: '1px solid rgba(255,255,255,0.06)' }}>
          <p style={{ fontSize: '0.9rem', color: COLORS.gray, margin: '0 0 0.5rem', fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
            No orders yet.
          </p>
          <p style={{ fontSize: '0.78rem', color: COLORS.gray, margin: '0 0 2rem', opacity: 0.6 }}>
            Orders placed on the storefront will appear here.
          </p>
          <a
            href={import.meta.env.VITE_STOREFRONT_URL || 'https://qrqy.shop'}
            style={{
              display: 'inline-block',
              padding: '0.75rem 2rem',
              border: `1px solid ${COLORS.white}`,
              color: COLORS.white,
              textDecoration: 'none',
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.18em',
            }}
          >
            SHOP THE DROP
          </a>
        </div>
      )}

      {/* Orders list */}
      {!loading && !error && orders.length > 0 && (
        <>
          <p style={{ fontSize: '0.7rem', color: COLORS.gray, letterSpacing: '0.1em', margin: 0 }}>
            {orders.length} {orders.length === 1 ? 'ORDER' : 'ORDERS'}
          </p>

          <div style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
            {orders.map((order) => (
              <OrderRow key={order.id} order={order} />
            ))}
          </div>

          {!API_BASE_URL && (
            <p style={{ fontSize: '0.72rem', color: COLORS.gray, opacity: 0.5, textAlign: 'center' }}>
              Showing mock data — set VITE_API_BASE_URL for live data.
            </p>
          )}
        </>
      )}
    </div>
  );
}
